import { Signal } from './Signal';

/**
 * Checks whether the specified object implements [[PropertyObserver]].
 * If its possible to observe its properties
 *
 * @param obj The object that should be tested.
 */
export function isPropertyObserver( obj:any ):obj is PropertyObserver {
	return obj && obj[ 'propertyChange' ] instanceof Signal;
}


/**
 * Describes the payload of the [[PropertyChangedSignal.propertyChanged]] observable.
 */
export interface PropertyChanged {
	name:string
}


/**
 * Types that implement this interface publish all property value changes as an [[Observable]].
 *
 * @see observeProperties that turns an object into a PropertyObserver
 */
export interface PropertyObserver {

	/**
	 * Raised when a property's value has changed.
	 *
	 * @event
	 */
	propertyChange:Signal<PropertyChanged>;
}



export function dependProperty<T, S>( target:T, targetProperty:( keyof T ), source:S, sourceProperty:( keyof S ) ) {


	// make sure the properties are observed
	let targetObserved = observeProperties( target, targetProperty );
	let sourceObserved = observeProperties( source, sourceProperty );

	// only subscript on valid target / source
	if( !targetObserved || !sourceObserved ) return;

	sourceObserved.propertyChange.filter( e => e.name == sourceProperty ).subscribe( () => {
		targetObserved.propertyChange.dispatch( { name:targetProperty as string } );
	});
}


/**
 * Observes a list of properties
 * @param target The model to observe properties on
 * @param properties The list of property name to observe
 */
export function observeProperties<T>(target:T, ...properties:(keyof T)[] ):T&PropertyObserver {

	// do not observe any null / undefined targets
	if( !target ) return;

	let t = target as T&PropertyObserver;

	// make sure signal exists and has proper functions, otherwise recreate
	if( !t.propertyChange || typeof t.propertyChange.subscribe != 'function' ) t.propertyChange = new Signal<PropertyChanged>();

	// if there are no properties take all properties of the target
	if( properties.length == 0 ) properties = Object.keys( t ) as (keyof T)[];

	// loop over properties an check if there are accessors
	let obj:any = {};
	for ( const prop of properties ) {

		// no accessors for propertyChanged
		if( prop == 'propertyChange' ) continue;

		// lookup prototype chain for property descriptor
		let c = t;
		let desc;
		while( c != null && desc == null ) {
			desc = Object.getOwnPropertyDescriptor( c, prop );
			c = Object.getPrototypeOf( c );
		}

		// if property does not exist on target add undefined accessor
		if( !desc ) {
			obj[ prop ] = undefined;
			continue;
		}

		// check if it has already a getter/setter - do nothing
		if( desc.get || desc.set ) continue;

		obj[ prop ] =  target[ prop ];
	}

	// generate accessors for all props
	for( let attr in obj ) {

		// create dynamic property
		Object.defineProperty( t, attr, {
			get() {
				return obj[ attr ];
			},
			set( value ) {
				let old = obj[ attr ];
				if( old !== value ) {
					obj[ attr ] = value;
					t.propertyChange.dispatch( { name:attr } );
				}
			},
			enumerable: true,
			configurable: true
		});
	}

	return t;
}




/**
 * A guard that checks whether the specified object implements [[ArrayObserver]].
 *
 * @param obj The object that should be tested.
 */
export function isArrayObserver( obj:any ):obj is ArrayObserver {
	return obj && obj[ 'arrayChange' ] instanceof Signal;
}


/**
 * Arrays that implement this interface observe changes on the array itself
 *
 */
export interface ArrayObserver {

	/**
	 * Raised when the items of the array have changed.
	 *
	 * @event
	 */
	arrayChange:Signal<{}>;
}

/**
 * Observes an array on changes
 */
export function observeArray( a:Array<any> ):Array<any>&ArrayObserver {

	// do not observe other than arrays
	if( !Array.isArray( a ) ) return;

	let t = a as Array<any>&ArrayObserver;

	// check if its already observed
	if( t.arrayChange ) return t;

	t.arrayChange = new Signal();

	for (const method of [ 'push', 'pop', 'shift', 'unshift', 'splice', 'sort', 'reverse' ] ) {
		const org = t[method];
		// overwrite function to inject signal notification
		t[method] = function ( ...args ) {
			const res = org.apply( this, args );
			t.arrayChange.dispatch({});
			return res;
		}
	}

	return t;
}
